/**
 * Text continuity — presentation twin of voice cold-open (S1) and engaged SPA (S2).
 *
 * Pure helpers only. No freestyle LLM essays, no invented pills.
 * Cold summary is FAITHFUL page summary: monologue → invites → clean topic.
 * Never dumps layout/section chrome ("Top of page", "On Your Website").
 */

import {
  buildEngagementBarLead,
  focusMatchesInventory,
  focusMatchesPageContext,
  isSpeakableContinuityTopic,
  rankQuestionsByFocus,
  type ConversationFocus,
} from "./conversation-focus.ts";

export type TextLandMode = "cold" | "engaged_related" | "engaged_unrelated";

export type TextLandPresentation = {
  mode: TextLandMode;
  /**
   * Single summary string for the prompt bar (static, no typewriter).
   * Empty → use default ask-about placeholder.
   */
  heroLines: string[];
  /** Side rail top kicker / continuation (short). */
  railLead: string;
  /** Bottom living-bar lead (aligned with continuity rules). */
  barLead: string;
  /** Page pool ordered for hero chips (inventory only). */
  rankedPagePills: string[];
  rankedSectionCurrent: string[];
  rankedSectionNext: string[];
};

export type TextLandInput = {
  focus?: ConversationFocus | null;
  path?: string | null;
  pageTopic?: string | null;
  monologue?: readonly string[];
  inviteLines?: readonly string[];
  sectionHeadings?: readonly string[];
  currentHeading?: string | null;
  nextHeading?: string | null;
  pagePool?: readonly string[];
  currentQuestions?: readonly string[];
  nextQuestions?: readonly string[];
};

/** Title-case ALL-CAPS headings so bar/rail don't read "On ON YOUR WEBSITE". */
export function titleishHeading(heading: string): string {
  const t = heading.replace(/\s+/g, " ").trim();
  if (!t) return "";
  if (t === t.toUpperCase() && t.length <= 48) {
    return t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return t;
}

/**
 * Layout / chrome headings that must never appear as page summary or bar lead.
 * Fleet-wide structural junk — not product names.
 */
export function isJunkSectionHeading(heading: string | null | undefined): boolean {
  const t = (heading ?? "").replace(/\s+/g, " ").trim().toLowerCase();
  if (!t || t.length < 2) return true;
  // Pure layout / scroll chrome
  if (
    /^(top of (the )?page|bottom of (the )?page|header|footer|nav|navigation|sidebar|menu|hero|banner|cta)$/i.test(
      t,
    )
  ) {
    return true;
  }
  // Generic "on your website / site" marketing blocks
  if (
    /^(on your (web)?site|your website|my website|this website|the website|website|home|homepage)$/i.test(
      t,
    )
  ) {
    return true;
  }
  // Ultra-generic crumbs
  if (/^(section|content|main|page|overview|intro|introduction)$/i.test(t)) {
    return true;
  }
  // Marketing taglines / imperatives — not section names for "Ask about X"
  if (
    /^(answer every visitor|stop |start |see why|see what|put your|before you go|powered by)/i.test(
      t,
    )
  ) {
    return true;
  }
  // Sentence-like (taglines often end with period or run long)
  if (/[.!?]$/.test(t) || t.split(" ").length > 5) return true;
  return false;
}

/** Strip voice pick/ask and re-tap tails for on-screen summary. */
export function stripVoiceChrome(text: string): string {
  return text
    .replace(/\s+/g, " ")
    .replace(
      /\s*Pick a question on the screen, or ask me anything\.?/gi,
      "",
    )
    .replace(/\s*If you need me again[^.]*\.?/gi, "")
    .replace(/\s*Just hit the AI Voice button\.?/gi, "")
    .replace(/\s*hit the AI Voice button\.?/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[.]+$/, (m) => (m.length > 1 ? "." : m));
}

function monoLines(mono: readonly string[] | undefined): string[] {
  return (mono ?? [])
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 12)
    .slice(0, 3);
}

/**
 * One wide summary line for the prompt bar (cold).
 * Faithful: monologue → invite → clean page topic. Never section dumps.
 */
function coldPromptSummary(input: TextLandInput): string {
  const mono = monoLines(input.monologue);
  if (mono.length > 0) {
    // Join to one wide line (wider bar, not taller stack).
    const joined = mono.join(" ").replace(/\s+/g, " ").trim();
    return joined.length > 180
      ? `${joined.slice(0, 177).replace(/\s+\S*$/, "")}…`
      : joined;
  }

  const invite = (input.inviteLines ?? [])
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter((s) => s.length >= 12)[0];
  if (invite) {
    return invite.length > 180
      ? `${invite.slice(0, 177).replace(/\s+\S*$/, "")}…`
      : invite;
  }

  // Only real section labels (never "Top of page" / "On Your Website").
  const goodSections = (input.sectionHeadings ?? [])
    .map((h) => titleishHeading(h))
    .filter((h) => h.length >= 6 && !isJunkSectionHeading(h))
    .filter(
      (h, i, a) =>
        a.findIndex((x) => x.toLowerCase() === h.toLowerCase()) === i,
    )
    .slice(0, 2);
  if (goodSections.length === 2) {
    return `Ask me about ${goodSections[0]} or ${goodSections[1]}.`;
  }
  if (goodSections.length === 1) {
    return `Ask me about ${goodSections[0]}.`;
  }

  const topic = (input.pageTopic ?? "").replace(/\s+/g, " ").trim();
  if (
    topic &&
    topic.length >= 3 &&
    topic.length <= 48 &&
    !isJunkSectionHeading(topic) &&
    !/^(home|homepage|my website|website|welcome)$/i.test(topic)
  ) {
    return `Ask me anything about ${topic}.`;
  }

  return "Ask me anything about this page.";
}

function coldHeroLines(input: TextLandInput): string[] {
  const s = coldPromptSummary(input);
  return s ? [s] : [];
}

function usableHeading(raw: string | null | undefined): string {
  const t = titleishHeading(raw ?? "");
  if (!t || isJunkSectionHeading(t)) return "";
  return t;
}

function coldRailLead(input: TextLandInput): string {
  const here = usableHeading(input.currentHeading);
  const ahead = usableHeading(input.nextHeading);
  if (here && ahead) return `${here} · next: ${ahead}`;
  if (here) return here;
  if (ahead) return `Coming up · ${ahead}`;
  const topic = (input.pageTopic ?? "").replace(/\s+/g, " ").trim();
  if (topic && topic.length <= 40 && !isJunkSectionHeading(topic)) return topic;
  return "On this page";
}

/**
 * Prompt-bar copy after engage — NOT voice speech templates.
 * Never "Related to {mangled question}. Want to continue with: {long pill}?"
 * Prefer monologue (faithful page summary), then a short clean thread hook.
 * Pills stay under the bar — not stuffed into the summary line.
 */
function engagedHeroLines(
  input: TextLandInput,
  related: boolean,
  _pill: string | null,
): string[] {
  void _pill;
  // Always prefer authored page summary when present (AI-site feel).
  const mono = monoLines(input.monologue);
  if (mono.length > 0) {
    const joined = mono.join(" ").replace(/\s+/g, " ").trim();
    return [
      joined.length > 160
        ? `${joined.slice(0, 157).replace(/\s+\S*$/, "")}…`
        : joined,
    ];
  }

  if (related) {
    const topic = (input.focus?.topicLabel ?? "").replace(/\s+/g, " ").trim();
    if (isSpeakableContinuityTopic(topic) && !looksLikeBrokenTopic(topic)) {
      // Short, human — not TTS "Want to continue with: …"
      const short =
        topic.length > 48
          ? `${topic.slice(0, 45).replace(/\s+\S*$/, "")}…`
          : topic;
      return [`Still exploring ${short} — ask me anything.`];
    }
    return ["Ask a follow-up, or pick a question below."];
  }

  // Engaged but this page is unrelated to the thread.
  return coldHeroLines(input);
}

/** Topic labels that read as broken English in the bar ("X help Y", bare verbs). */
function looksLikeBrokenTopic(topic: string): boolean {
  const t = topic.replace(/\s+/g, " ").trim();
  if (!t || t.length < 4) return true;
  // Missing "how" / infinitive: "BlueHatAI help websites answer…"
  if (/\b(help|helps|show|shows|make|makes|let|lets)\s+\w+/i.test(t) && !/^how\b/i.test(t)) {
    // Allow clean gerunds: "helping visitors convert"
    if (!/\b(ing)\b/i.test(t.split(/\s+/)[0] ?? "") && !/\w+ing\b/i.test(t)) {
      return true;
    }
  }
  // Still looks like a chopped question
  if (/^(does|do|can|what|how does|how do)\b/i.test(t)) return true;
  return false;
}

function engagedRailLead(input: TextLandInput, related: boolean): string {
  const here = usableHeading(input.currentHeading);
  const topic =
    related && isSpeakableContinuityTopic(input.focus?.topicLabel)
      ? (input.focus!.topicLabel ?? "").replace(/\s+/g, " ").trim()
      : null;

  if (topic) {
    if (here) return `Related to ${topic} · ${here}`;
    const page = (input.pageTopic ?? "").replace(/\s+/g, " ").trim();
    if (page && page.length <= 40 && !isJunkSectionHeading(page)) {
      return `Related to ${topic} · ${page}`;
    }
    return `Related to ${topic}`;
  }

  return coldRailLead(input);
}

/**
 * Resolve hero + rail + bar presentation for cold or engaged land.
 * Fleet-wide match rules only — no product dictionary.
 */
export function resolveTextLandPresentation(
  input: TextLandInput,
): TextLandPresentation {
  const focus = input.focus ?? null;
  const pagePool = input.pagePool ?? [];
  const currentQs = input.currentQuestions ?? [];
  const nextQs = input.nextQuestions ?? [];
  const inventory = [...currentQs, ...nextQs, ...pagePool];

  const inventoryMatch =
    !!focus && focusMatchesInventory(inventory, focus);
  const pageMatch =
    !!focus &&
    focusMatchesPageContext(focus, {
      pageTopic: input.pageTopic,
      path: input.path,
      inventory,
    });
  const related = inventoryMatch || pageMatch;
  const engaged = !!focus && (focus.lastUser?.trim().length ?? 0) > 0;

  let mode: TextLandMode = "cold";
  if (engaged) {
    mode = related ? "engaged_related" : "engaged_unrelated";
  }

  const rankedPage = rankQuestionsByFocus(pagePool, related ? focus : null);
  const rankedCurrent = rankQuestionsByFocus(
    currentQs,
    related ? focus : null,
  );
  const rankedNext = rankQuestionsByFocus(nextQs, related ? focus : null);
  const topPill = rankedCurrent[0] ?? rankedPage[0] ?? null;

  let heroLines: string[];
  let railLead: string;
  if (mode === "cold") {
    heroLines = coldHeroLines(input);
    railLead = coldRailLead(input);
  } else if (mode === "engaged_related") {
    heroLines = engagedHeroLines(input, true, topPill);
    railLead = engagedRailLead(input, true);
  } else {
    heroLines = engagedHeroLines(input, false, topPill);
    railLead = engagedRailLead(input, false);
  }

  // Bar lead: skip junk headings so "On On Your Website" never appears.
  const barHere = usableHeading(input.currentHeading) || null;
  const barAhead = usableHeading(input.nextHeading) || null;
  const barLead = buildEngagementBarLead({
    currentHeading: barHere,
    nextHeading: barAhead,
    focus,
    inventory,
  });

  return {
    mode,
    heroLines,
    railLead,
    barLead,
    rankedPagePills: rankedPage,
    rankedSectionCurrent: rankedCurrent,
    rankedSectionNext: rankedNext,
  };
}
